import React, { useEffect, useMemo, useState } from 'react';
import DataGrid, { Column, Editing } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { GroupItem, Label, SimpleItem } from "devextreme-react/form";
import { FieldDataChangedEvent } from 'devextreme/ui/form';
import ITirage from "../../models/ITirage";
import IGestionTirage from '../../models/IGestionTirage';
import { Mode } from "../../hooks/useCustomDataSource";

export interface IGestionTiragesResultatsProps {
  mode: Mode;
  selectedTirage: IGestionTirage;
  onFieldDataChanged: (e: FieldDataChangedEvent) => void;
}

export default function GestionTiragesResultats({
  mode,
  selectedTirage,
  onFieldDataChanged,
}: IGestionTiragesResultatsProps) {
  const [dateRappel, setDateRappel] = useState<Date | undefined>();
  const [duRang, setDuRang] = useState<number | undefined>();
  const [auRang, setAuRang] = useState<number | undefined>();

  const resultatsFiltres = useMemo(() => {
    let rows = selectedTirage?.resultats ?? [];
    if (duRang != null) {
      rows = rows.filter(r => r.rang >= duRang);
    }
    if (auRang != null) {
      rows = rows.filter(r => r.rang <= auRang);
    }
    return rows;
  }, [selectedTirage?.resultats, duRang, auRang]);

  const dateRappelOptions = useMemo(() => ({
    width: "55%",
    type: "date",
    displayFormat: "dd-MM-yyyy",
    value: dateRappel,
    onValueChanged: (e: { value?: Date }) => setDateRappel(e.value),
  }), [dateRappel]);

  const duRangOptions = useMemo(() => ({
    value: duRang,
    onValueChanged: (e: { value?: number }) => setDuRang(e.value),
  }), [duRang]);

  const auRangOptions = useMemo(() => ({
    value: auRang,
    onValueChanged: (e: { value?: number }) => setAuRang(e.value),
  }), [auRang]);

  const datesAppelsOptions = useMemo(() => ({
    text: "Appliquer les dates d'appels",
    disabled: mode === Mode.Lecture || !dateRappel,
  }), [mode, dateRappel]);

  useEffect(() => {
    setDateRappel(undefined);
    setDuRang(undefined);
    setAuRang(undefined);
  }, [selectedTirage?.idTirage]);

  return (
    <TabItem title="Résultats du tirage">
      <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedTirage || {} as ITirage} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
        <GroupItem colCount={4}>
          <SimpleItem key={"frm-date-rappel"} editorType={"dxDateBox"}
            editorOptions={dateRappelOptions}
          >
            <Label text={"Date rappel"} />
          </SimpleItem>
          <SimpleItem key={"frm-du-rang"} editorType={"dxNumberBox"}
            editorOptions={duRangOptions}
          >
            <Label text={"Du rang"} />
          </SimpleItem>
          <SimpleItem key={"frm-au-rang"} editorType={"dxNumberBox"}
            editorOptions={auRangOptions}
          >
            <Label text={"Au rang"} />
          </SimpleItem>
          <SimpleItem key={"frm-appliquer-dates-appels"} editorType={"dxCheckBox"} editorOptions={datesAppelsOptions} />
        </GroupItem>
        <GroupItem>
          <DataGrid
            showBorders={true}
            allowColumnResizing={true}
            dataSource={resultatsFiltres}
            keyExpr="idTirageDetail"
            allowColumnReordering={false}
            noDataText={"Aucun résultat"}
          >
            <Editing mode="cell" allowAdding={false} allowUpdating={false} allowDeleting={false} />
            <Column dataField={"rang"} dataType='number' caption={"Rang"} />
            <Column dataField={"client"} dataType='string' caption={"Client"} />
            <Column dataField={"adresse"} dataType='string' caption={"Adresse"} />
            <Column dataField={"ville"} dataType='string' caption={"Ville"} />
            <Column dataField={"telephone"} dataType='string' caption={"Téléphone"} />
            <Column dataField={"dateAppel"} dataType='date' caption={"Date appel"} format={"dd-MM-yyyy"} />
            <Column dataField={"substitut"} dataType='string' caption={"Substitut"} />
            <Column dataField={"avise"} dataType='boolean' caption={"Avisé"} />
          </DataGrid>
        </GroupItem>
      </Form>
    </TabItem>
  );
}
